import React, { useEffect, useState } from "react";
import MsgSendIcon from "@styles/all-images/icons/msgsend.svg";
import LiveChatTerminate from "@/pages/LiveChat/GroupChat/LiveChatTerminate";
import { useNavigate } from "react-router-dom";
import { useLiveChatState } from "../Provider/LiveChatStateProvider";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch, RootState } from "@/store";
import { GroupInfoResponse } from "../types/api-response-types";
import GroupChatSkeleton from "../components/skeletons/GroupChatSkeleton";
import ChatMessages from "../components/ChatMessages";
import PinIcon from "@styles/all-images/icons/pin.svg";
import closeIcon from "@styles/all-images/icons/close.svg";
import { Message } from "../types/chat";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";

interface GroupChatScreenProps {}

const GroupChatScreen: React.FC<GroupChatScreenProps> = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isGroupTerminateModalOpen, setIsGroupTerminateModalOpen] =
    useState(false);
  const { currentScreen } = useLiveChatState();
  const [groupInfo, setGroupInfo] = useState<GroupInfoResponse | null>(null);
  const [isGroupInfoLoading, setIsGroupInfoLoading] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  const handleGroupTerminate = () => {
    setIsGroupTerminateModalOpen(true);
  };

  const handleCloseGroupTerminateModal = () => {
    setIsGroupTerminateModalOpen(false);
  };

  const handleReplyClick = (message: Message) => {
    setSelectedMessage(message);
    setShowThread(true);
  };

  const handleCloseThread = () => {
    setShowThread(false);
    setSelectedMessage(null);
  };

  const getGroupInfo = async (groupId: number) => {
    try {
      setIsGroupInfoLoading(true);
      const { authenticatedCall } = actionsHandler("getGroupInfo");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [groupId.toString() + "/"],
        }),
      );
      if (response.ok) {
        setGroupInfo(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsGroupInfoLoading(false);
    }
  };

  const handleTerminateSuccess = () => {
    if (currentScreen?.data.group) {
      getGroupInfo(currentScreen.data.group.room_id);
    }
  };

  useEffect(() => {
    if (currentScreen?.data.group) {
      getGroupInfo(currentScreen?.data.group?.room_id);
    }
  }, [currentScreen]);

  if (isGroupInfoLoading || !groupInfo) {
    return <GroupChatSkeleton />;
  }

  return (
    <div
      className={`tw-grid ${showThread ? "md:tw-grid-cols-2" : "md:tw-grid-cols-1"} tw-gap-4`}
    >
      <div className="tw-w-full tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-rounded-lg">
        <div className="tw-border-b tw-border-[#CBD5E1]">
          <div className="tw-p-4 tw-mx-4 tw-flex tw-items-start tw-justify-between">
            <div className="tw-flex tw-items-center tw-space-x-3">
              <div className="tw-w-10 tw-h-10 tw-bg-[#ECF1F8] tw-rounded-full tw-flex tw-items-center tw-justify-center">
                <span className="tw-text-[#146C94] tw-font-semibold">
                  {groupInfo?.name.charAt(0)}
                </span>
              </div>
              <div>
                <h3 className="tw-text-[#232323] tw-text-base tw-font-semibold">
                  {groupInfo?.name}
                </h3>
                <button
                  onClick={() =>
                    navigate(`/management-teams-group-members/${groupInfo?.id}`)
                  }
                  className="tw-text-sm tw-text-[#475569] tw-font-normal"
                >
                  {groupInfo?.members.length} members
                </button>
              </div>
            </div>
            {!groupInfo?.is_terminated &&
              user?.user_type.id === UserTierType.Tier3 && (
                <div className="tw-flex tw-items-center tw-gap-4">
                  <button
                    onClick={handleGroupTerminate}
                    className="tw-flex tw-text-base tw-font-medium tw-text-[#ED4040] tw-items-center tw-border tw-border-[#ED4040] tw-py-3 tw-px-4 tw-rounded-lg tw-gap-2"
                  >
                    Terminate Chat
                  </button>
                </div>
              )}
          </div>
        </div>

        <ChatMessages
          onReplyClick={handleReplyClick}
          isGroup={true}
          groupInfo={groupInfo}
          onAddMember={() =>
            navigate(`/add-people-group-live-chat/${groupInfo.id}`)
          }
        />
      </div>

      {showThread && selectedMessage && (
        <div className="tw-w-full tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-rounded-lg">
          <div className="tw-mx-4 tw-py-4 tw-border-[#CBD5E1]">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex tw-items-center tw-space-x-3">
                <img src={PinIcon} alt="Thread" className="tw-w-7 tw-h-7" />
                <h3 className="tw-text-[#232323] tw-text-base tw-font-semibold">
                  Thread
                </h3>
              </div>
              <button onClick={handleCloseThread}>
                <img src={closeIcon} alt="Close" className="tw-w-5 tw-h-5" />
              </button>
            </div>
          </div>

          <ChatMessages threadMessageId={selectedMessage?.id} isThread={true} />
        </div>
      )}

      {isGroupTerminateModalOpen && (
        <LiveChatTerminate
          onClose={handleCloseGroupTerminateModal}
          onSuccess={handleTerminateSuccess}
        />
      )}
    </div>
  );
};

export default GroupChatScreen;
